import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { firestore } from '../firebase';
import {
  collection, getDocs, doc, updateDoc, deleteDoc,
  setDoc, serverTimestamp, Timestamp, onSnapshot,
} from 'firebase/firestore';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || '', withCredentials: true });

const PINK  = '#FF1F6A';
const CARD  = '#111';
const BDR   = 'rgba(255,255,255,0.07)';
const MUTED = '#555';
const WHITE = '#EAEAEA';
const ACC   = '#C8FF00';

const avatar = (p) => p?.photos?.[0] || `https://api.dicebear.com/7.x/thumbs/svg?seed=${p?.uid || 'x'}`;
const fmt    = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric' });
};
const isBoosted = (p) => p.boosted && p.boostedUntil?.toDate?.() > new Date();

const BOOST_OPTIONS = [
  { label: '24 heures',  hours: 24  },
  { label: '3 jours',   hours: 72  },
  { label: '7 jours',   hours: 168 },
  { label: '30 jours',  hours: 720 },
];

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: CARD, borderRadius: 12, padding: '18px 22px', border: `1px solid ${BDR}`, minWidth: 130 }}>
      <div style={{ fontSize: '2rem', fontWeight: 900, color: color || WHITE, letterSpacing: '-0.04em', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div style={{ fontSize: '0.76rem', color: MUTED, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function Badge({ label, color, bg }) {
  return (
    <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700, color, background: bg, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

// Inject Leaflet CSS once
function useLeafletCSS() {
  useEffect(() => {
    if (document.getElementById('leaflet-css-admin')) return;
    const link = document.createElement('link');
    link.id   = 'leaflet-css-admin';
    link.rel  = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
  }, []);
}

export default function VibeeAdminSection() {
  useLeafletCSS();
  const [tab, setTab]         = useState('users'); // users | reports | orders | map | subscribed
  const [users, setUsers]     = useState([]);
  const [reports, setReports] = useState([]);
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [filter, setFilter]   = useState('all'); // all | boosted | banned | active | subscribed
  const [modal, setModal]     = useState(null);  // null | { type, user }
  const [boostHours, setBoostHours] = useState(168);
  const [working, setWorking] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const unsubUsersRef = useRef(null);

  const [showAllReports, setShowAllReports] = useState(false);
  const [resolvingId,    setResolvingId]    = useState(null);
  const [pendingPhotos,  setPendingPhotos]  = useState([]);
  const [photoWorking,   setPhotoWorking]   = useState(null);
  const [snapError,      setSnapError]      = useState(null);

  const loadReportsOrders = async (allReports = showAllReports) => {
    const [rResp, oSnap, pResp] = await Promise.all([
      api.get(`/api/vibee-admin/reports${allReports ? '?all=true' : ''}`).catch(() => ({ data: { reports: [] } })),
      getDocs(collection(firestore, 'vibeeOrders')).catch(() => ({ docs: [] })),
      api.get('/api/vibee-admin/pending-photos').catch(() => ({ data: { profiles: [] } })),
    ]);
    setReports(rResp.data.reports || []);
    setPendingPhotos(pResp.data.profiles || []);
    const sortByAt = (docs) => docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.at?.toMillis?.() ?? 0) - (a.at?.toMillis?.() ?? 0));
    setOrders(sortByAt(oSnap.docs));
  };

  const subscribeUsers = () => {
    if (unsubUsersRef.current) unsubUsersRef.current();
    setSnapError(null);
    setLoading(true);
    const q = collection(firestore, 'vibeeProfiles');
    unsubUsersRef.current = onSnapshot(q, (snap) => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      all.sort((a, b) => {
        const ta = a.createdAt?.toMillis?.() ?? 0;
        const tb = b.createdAt?.toMillis?.() ?? 0;
        return tb - ta;
      });
      setUsers(all);
      setSnapError(null);
      setLoading(false);
    }, (err) => {
      console.error('VibeeAdminSection onSnapshot error:', err.code, err.message);
      setSnapError(err.code || err.message || 'Erreur Firestore');
      setLoading(false);
    });
  };

  const approvePhoto = async (uid) => {
    setPhotoWorking(uid + '_approve');
    await api.patch(`/api/vibee-admin/photos/${uid}/approve`).catch(() => {});
    setPendingPhotos(prev => prev.filter(p => p.uid !== uid));
    setPhotoWorking(null);
  };

  const rejectPhoto = async (uid) => {
    setPhotoWorking(uid + '_reject');
    await api.patch(`/api/vibee-admin/photos/${uid}/reject`).catch(() => {});
    setPendingPhotos(prev => prev.filter(p => p.uid !== uid));
    setPhotoWorking(null);
  };

  const load = () => { subscribeUsers(); loadReportsOrders(); };

  useEffect(() => {
    subscribeUsers();
    loadReportsOrders();
    return () => { if (unsubUsersRef.current) unsubUsersRef.current(); };
  }, []);

  const isSubscribed = (u) => u.subscriptionActive && u.subscriptionEnd?.toDate?.() > new Date();

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchQ = !q || (u.name||'').toLowerCase().includes(q) || (u.email||'').toLowerCase().includes(q) || (u.city||'').toLowerCase().includes(q);
    const matchF = filter === 'all'        ? true
      : filter === 'boosted'    ? isBoosted(u)
      : filter === 'banned'     ? u.banned
      : filter === 'subscribed' ? isSubscribed(u)
      : !u.banned && !isBoosted(u);
    return matchQ && matchF;
  });

  const revenue = orders.reduce((sum, o) => sum + (o.amount || 0), 0);

  const stats = {
    total:      users.length,
    active:     users.filter(u => !u.banned).length,
    boosted:    users.filter(u => isBoosted(u)).length,
    banned:     users.filter(u => u.banned).length,
    subscribed: users.filter(u => isSubscribed(u)).length,
    reports:    reports.filter(r => !r.resolved).length,
    revenue,
  };

  const applyBoost = async () => {
    if (!modal?.user) return;
    setWorking(true);
    const until = new Date(Date.now() + boostHours * 3600 * 1000);
    await updateDoc(doc(firestore, 'vibeeProfiles', modal.user.id), {
      boosted: true,
      boostedUntil: Timestamp.fromDate(until),
      boostedAt: serverTimestamp(),
    });
    setWorking(false);
    setModal(null);
    load();
  };

  const removeBoost = async (uid) => {
    await updateDoc(doc(firestore, 'vibeeProfiles', uid), { boosted: false, boostedUntil: null });
    load();
  };

  const toggleBan = async (u) => {
    const newBanned = !u.banned;
    try {
      try {
        await api.patch(`/api/vibee-admin/users/${u.id}/ban`, { banned: newBanned });
      } catch {
        // Profil demo sans compte Auth — met à jour seulement Firestore
        await updateDoc(doc(firestore, 'vibeeProfiles', u.id), { banned: newBanned });
      }
      load();
    } catch (err) {
      alert(`Erreur lors du ${newBanned ? 'bannissement' : 'débannissement'} : ${err.message}`);
    }
  };

  const deleteUser = async (u) => {
    try {
      // Supprime Auth + profil Firestore + swipes via le serveur
      await api.delete(`/api/vibee-admin/users/${u.id}`);
    } catch {
      // Si l'utilisateur demo n'a pas de compte Auth, on supprime juste Firestore
      await deleteDoc(doc(firestore, 'vibeeProfiles', u.id)).catch(() => {});
    }
    setConfirmDel(null);
    load();
  };

  const handleReport = async (r, action) => {
    setResolvingId(r.id);
    try {
      await api.patch(`/api/vibee-admin/reports/${r.id}/resolve`, { action, reportedUid: r.reported });
      loadReportsOrders(showAllReports);
    } catch { }
    setResolvingId(null);
  };

  const inp = { width: '100%', padding: '10px 14px', borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: `1px solid ${BDR}`, color: WHITE, fontSize: '0.88rem', outline: 'none', fontFamily: 'inherit' };
  const btn = (bg, color='#000') => ({ padding: '8px 16px', borderRadius: 8, background: bg, color, fontWeight: 700, fontSize: '0.8rem', border: 'none', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' });

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1100, color: WHITE, fontFamily: 'inherit' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 900, margin: 0, fontStyle: 'italic' }}>
            Vy<span style={{ color: PINK }}>b</span>me
            <span style={{ color: MUTED, fontWeight: 400, fontSize: '1rem', marginLeft: 10, fontStyle: 'normal' }}>Admin</span>
          </h1>
          <p style={{ color: MUTED, fontSize: '0.8rem', margin: '3px 0 0' }}>Gestion des utilisateurs · Modération · Boost</p>
        </div>
        <button onClick={load} style={{ ...btn('rgba(255,255,255,0.07)', WHITE), marginLeft: 'auto' }}>↻ Actualiser</button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 28 }}>
        <StatCard label="Total inscrits"    value={stats.total}      color={WHITE} />
        <StatCard label="Actifs"            value={stats.active}     color='#4ade80' />
        <StatCard label="Abonnés Vybme+ 💎" value={stats.subscribed} color={PINK} />
        <StatCard label="Boostés ⚡"       value={stats.boosted}    color={ACC} />
        <StatCard label="Bannis"            value={stats.banned}     color='#f87171' />
        <StatCard label="Signalements"      value={stats.reports}    color={PINK} />
        <StatCard label="Revenus totaux"    value={`${(stats.revenue / 100).toFixed(2)} $`} color='#a78bfa' />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {[['users', '👥 Utilisateurs'], ['map', '🗺 Carte'], ['reports', '🚨 Signalements'], ['photos', `📸 Photos${pendingPhotos.length ? ` (${pendingPhotos.length})` : ''}`], ['orders', '💳 Achats']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ ...btn(tab === id ? PINK : 'transparent', tab === id ? '#fff' : MUTED), borderRadius: 8, padding: '8px 18px' }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── USERS TAB ── */}
      {tab === 'users' && (
        <>
          {/* Search + Filter */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="🔍  Rechercher par nom, email, ville…"
              style={{ ...inp, maxWidth: 340 }} />
            <div style={{ display: 'flex', gap: 6 }}>
              {[['all','Tous'], ['active','Actifs'], ['subscribed','Abonnés 💎'], ['boosted','Boostés ⚡'], ['banned','Bannis']].map(([v, l]) => (
                <button key={v} onClick={() => setFilter(v)}
                  style={{ ...btn(filter === v ? ACC : 'rgba(255,255,255,0.06)', filter === v ? '#000' : MUTED), fontSize: '0.76rem' }}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {snapError && (
            <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ color: '#f87171', fontWeight: 700, fontSize: '0.88rem' }}>
                ⚠ Erreur Firestore : {snapError}
              </span>
              <button onClick={subscribeUsers} style={{ ...btn('rgba(239,68,68,0.2)', '#f87171'), marginLeft: 'auto', fontSize: '0.78rem' }}>
                ↻ Réessayer
              </button>
            </div>
          )}

          {loading ? (
            <p style={{ color: MUTED }}>Chargement…</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, border: `1px solid ${BDR}`, borderRadius: 14, overflow: 'hidden' }}>
              {/* Table header */}
              <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr 80px 90px 120px 110px 160px', gap: 0, padding: '10px 16px', background: 'rgba(255,255,255,0.03)', borderBottom: `1px solid ${BDR}`, fontSize: '0.7rem', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <span></span>
                <span>Profil</span>
                <span>Âge</span>
                <span>Genre</span>
                <span>Ville</span>
                <span>Inscrit le</span>
                <span>Actions</span>
              </div>

              {filtered.length === 0 && (
                <div style={{ padding: '32px', textAlign: 'center', color: MUTED }}>Aucun utilisateur trouvé.</div>
              )}

              {filtered.map((u, i) => (
                <div key={u.id} style={{ display: 'grid', gridTemplateColumns: '48px 1fr 80px 90px 120px 110px 160px', gap: 0, padding: '12px 16px', borderBottom: i < filtered.length - 1 ? `1px solid ${BDR}` : 'none', alignItems: 'center', background: u.banned ? 'rgba(248,113,113,0.04)' : isBoosted(u) ? 'rgba(200,255,0,0.03)' : 'transparent', transition: 'background 0.1s' }}
                  onMouseEnter={e => { if (!u.banned && !isBoosted(u)) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = u.banned ? 'rgba(248,113,113,0.04)' : isBoosted(u) ? 'rgba(200,255,0,0.03)' : 'transparent'; }}>

                  {/* Avatar */}
                  <img src={avatar(u)} alt="" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', border: isBoosted(u) ? `2px solid ${ACC}` : u.banned ? '2px solid #f87171' : `1px solid ${BDR}` }} />

                  {/* Name + email + badges */}
                  <div style={{ minWidth: 0, paddingRight: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {u.name || u.email?.split('@')[0] || u.uid?.slice(0, 10) + '…'}
                      </span>
                      {isSubscribed(u) && <Badge label="💎 Vybme+" color="#fff" bg="rgba(255,31,106,0.7)" />}
                      {isBoosted(u)    && <Badge label="⚡ Boosté" color="#000" bg={ACC} />}
                      {u.banned        && <Badge label="Banni"     color="#fff" bg="#EF4444" />}
                      {!u.name         && <Badge label="⚠ sans nom" color="#fff" bg="rgba(245,158,11,0.6)" />}
                    </div>
                    <div style={{ fontSize: '0.74rem', color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email || u.uid?.slice(0, 14) + '…'}</div>
                  </div>

                  <span style={{ fontSize: '0.85rem', color: WHITE }}>{u.age || '—'}</span>
                  <span style={{ fontSize: '0.82rem', color: MUTED }}>{u.gender || '—'}</span>
                  <span style={{ fontSize: '0.82rem', color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.city || '—'}</span>
                  <span style={{ fontSize: '0.76rem', color: MUTED }}>{fmt(u.createdAt)}</span>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    <button onClick={() => setModal({ type: 'view', user: u })}
                      style={{ ...btn('rgba(255,255,255,0.08)', WHITE), padding: '6px 10px', fontSize: '0.75rem' }}>👁</button>
                    <button onClick={() => setModal({ type: 'boost', user: u })}
                      title={isBoosted(u) ? `Boosté jusqu'au ${fmt(u.boostedUntil)}` : 'Booster'}
                      style={{ ...btn(isBoosted(u) ? 'rgba(200,255,0,0.15)' : 'rgba(200,255,0,0.08)', isBoosted(u) ? ACC : '#888'), padding: '6px 10px', fontSize: '0.75rem', border: isBoosted(u) ? `1px solid ${ACC}44` : 'none' }}>⚡</button>
                    <button onClick={() => toggleBan(u)}
                      style={{ ...btn(u.banned ? 'rgba(74,222,128,0.12)' : 'rgba(248,113,113,0.1)', u.banned ? '#4ade80' : '#f87171'), padding: '6px 10px', fontSize: '0.75rem' }}>
                      {u.banned ? '✓' : '🚫'}
                    </button>
                    <button onClick={() => setConfirmDel(u)}
                      style={{ ...btn('rgba(239,68,68,0.1)', '#f87171'), padding: '6px 10px', fontSize: '0.75rem' }}>🗑</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── MAP TAB ── */}
      {tab === 'map' && (() => {
        const mapped = users.filter(u => u.lat && u.lng);
        const center = mapped.length > 0
          ? [mapped.reduce((s, u) => s + u.lat, 0) / mapped.length, mapped.reduce((s, u) => s + u.lng, 0) / mapped.length]
          : [46.5, -73.5];
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
              <p style={{ color: MUTED, fontSize: '0.84rem', margin: 0 }}>
                <strong style={{ color: WHITE }}>{mapped.length}</strong> usager{mapped.length !== 1 ? 's' : ''} localisé{mapped.length !== 1 ? 's' : ''} sur {users.length} inscrits
              </p>
              <div style={{ display: 'flex', gap: 12, fontSize: '0.76rem', color: MUTED }}>
                <span>🔴 Actif</span>
                <span style={{ opacity: 0.5 }}>⬤ Banni</span>
                <span style={{ color: ACC }}>⚡ Boosté</span>
              </div>
            </div>
            <div style={{ height: 580, borderRadius: 14, overflow: 'hidden', border: `1px solid ${BDR}` }}>
              <MapContainer center={center} zoom={mapped.length > 0 ? 7 : 5} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  attribution='© <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {mapped.map(u => {
                  const color = u.banned ? '#9CA3AF' : isBoosted(u) ? '#C8FF00' : PINK;
                  return (
                    <CircleMarker
                      key={u.id}
                      center={[u.lat, u.lng]}
                      radius={isBoosted(u) ? 10 : 7}
                      pathOptions={{ color, fillColor: color, fillOpacity: 0.85, weight: isBoosted(u) ? 2.5 : 1.5, opacity: 0.9 }}
                    >
                      <Popup>
                        <div style={{ fontFamily: 'system-ui, sans-serif', minWidth: 160 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <img src={avatar(u)} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{u.name || '—'}</div>
                              <div style={{ fontSize: '0.75rem', color: '#888' }}>{u.age ? `${u.age} ans` : ''}{u.gender ? ` · ${u.gender}` : ''}</div>
                            </div>
                          </div>
                          <div style={{ fontSize: '0.78rem', color: '#555', marginBottom: 4 }}>📍 {u.city || '—'}</div>
                          {u.email && <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: 4 }}>{u.email}</div>}
                          {u.banned    && <div style={{ fontSize: '0.72rem', color: '#EF4444', fontWeight: 700 }}>🚫 Banni</div>}
                          {isBoosted(u) && <div style={{ fontSize: '0.72rem', color: '#84cc16', fontWeight: 700 }}>⚡ Boosté</div>}
                          <button
                            onClick={() => { setModal({ type: 'view', user: u }); }}
                            style={{ marginTop: 8, width: '100%', padding: '6px 10px', borderRadius: 7, background: PINK, color: '#fff', border: 'none', fontWeight: 700, fontSize: '0.76rem', cursor: 'pointer' }}>
                            Voir le profil
                          </button>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </MapContainer>
            </div>
            {mapped.length === 0 && (
              <p style={{ color: MUTED, fontSize: '0.84rem', textAlign: 'center', marginTop: 16 }}>
                Aucun usager n'a encore activé la géolocalisation.
              </p>
            )}
          </div>
        );
      })()}

      {/* ── REPORTS TAB ── */}
      {tab === 'reports' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
            <p style={{ color: MUTED, fontSize: '0.8rem' }}>
              {reports.filter(r => !r.resolved).length} signalement{reports.filter(r => !r.resolved).length !== 1 ? 's' : ''} en attente
            </p>
            <button onClick={() => { const next = !showAllReports; setShowAllReports(next); loadReportsOrders(next); }}
              style={{ ...btn(showAllReports ? 'rgba(200,255,0,0.1)' : 'rgba(255,255,255,0.06)', showAllReports ? ACC : MUTED), fontSize: '0.75rem', padding: '6px 14px' }}>
              {showAllReports ? 'Afficher non-résolus' : 'Afficher tous'}
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {reports.length === 0 && <p style={{ color: MUTED }}>Aucun signalement en attente. 👍</p>}
            {reports.map(r => {
              const REASON_LABELS = { fake: 'Faux profil', harassment: 'Harcèlement', inappropriate: 'Contenu inapproprié', spam: 'Spam', underage: 'Mineur', other: 'Autre' };
              const isWorking = resolvingId === r.id;
              return (
                <div key={r.id} style={{ background: CARD, border: `1px solid ${r.resolved ? BDR : 'rgba(255,31,106,0.25)'}`, borderRadius: 14, padding: '16px 20px', opacity: r.resolved ? 0.55 : 1 }}>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    {/* Avatar profil signalé */}
                    <div style={{ flexShrink: 0 }}>
                      <img src={r.reportedPhoto || `https://api.dicebear.com/7.x/thumbs/svg?seed=${r.reported}`} alt=""
                        style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${r.resolved ? BDR : 'rgba(255,31,106,0.4)'}` }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Badges statut */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6, alignItems: 'center' }}>
                        <span style={{ fontWeight: 800, fontSize: '0.88rem', color: WHITE }}>{r.reportedName}</span>
                        <Badge label={REASON_LABELS[r.reason] || r.reason} color={PINK} bg="rgba(255,31,106,0.1)" />
                        {r.reportedBanned   && <Badge label="Banni"    color="#f87171" bg="rgba(239,68,68,0.1)" />}
                        {r.reportedSuspended && <Badge label="Suspendu" color="#fbbf24" bg="rgba(251,191,36,0.1)" />}
                        {r.resolved && <Badge label={`Résolu — ${r.resolvedAction || '?'}`} color="#4ade80" bg="rgba(74,222,128,0.1)" />}
                        <span style={{ fontSize: '0.7rem', color: MUTED, marginLeft: 'auto' }}>{r.at ? new Date(r.at).toLocaleDateString('fr-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                      </div>
                      {/* Détail du signalement */}
                      {r.detail && <p style={{ fontSize: '0.8rem', color: '#888', lineHeight: 1.5, marginBottom: 10, fontStyle: 'italic' }}>"{r.detail}"</p>}
                      <p style={{ fontSize: '0.7rem', color: MUTED, marginBottom: 10 }}>Signalé par : <code style={{ color: '#666', fontSize: '0.65rem' }}>{r.reporter}</code></p>
                      {/* Actions */}
                      {!r.resolved && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button onClick={() => handleReport(r, 'dismiss')} disabled={isWorking}
                            style={{ ...btn('rgba(255,255,255,0.06)', MUTED), opacity: isWorking ? 0.5 : 1 }}>
                            ✓ Ignorer
                          </button>
                          <button onClick={() => handleReport(r, 'warn')} disabled={isWorking}
                            style={{ ...btn('rgba(251,191,36,0.1)', '#fbbf24'), opacity: isWorking ? 0.5 : 1 }}>
                            ⚠️ Avertir
                          </button>
                          <button onClick={() => handleReport(r, 'suspend')} disabled={isWorking}
                            style={{ ...btn('rgba(249,115,22,0.1)', '#f97316'), opacity: isWorking ? 0.5 : 1 }}>
                            🔒 Suspendre
                          </button>
                          <button onClick={() => { if (window.confirm(`Bannir définitivement ${r.reportedName} ?`)) handleReport(r, 'ban'); }} disabled={isWorking}
                            style={{ ...btn('rgba(239,68,68,0.1)', '#f87171'), opacity: isWorking ? 0.5 : 1 }}>
                            🚫 Bannir
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── PHOTOS TAB ── */}
      {tab === 'photos' && (
        <div>
          <p style={{ color: MUTED, fontSize: '0.82rem', marginBottom: 16 }}>
            {pendingPhotos.length === 0
              ? 'Aucune photo en attente de révision. ✅'
              : `${pendingPhotos.length} profil${pendingPhotos.length > 1 ? 's' : ''} avec des photos à réviser`}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {pendingPhotos.map(p => {
              const isApproving = photoWorking === p.uid + '_approve';
              const isRejecting = photoWorking === p.uid + '_reject';
              return (
                <div key={p.uid} style={{ background: CARD, border: `1px solid rgba(255,31,106,0.25)`, borderRadius: 14, padding: '16px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <img src={p.photos?.[0] || `https://api.dicebear.com/7.x/thumbs/svg?seed=${p.uid}`} alt=""
                      style={{ width: 42, height: 42, borderRadius: '50%', objectFit: 'cover', border: `2px solid rgba(255,31,106,0.3)` }} />
                    <div>
                      <p style={{ fontWeight: 800, fontSize: '0.9rem', color: WHITE, marginBottom: 2 }}>{p.name || 'Sans nom'}</p>
                      <p style={{ fontSize: '0.72rem', color: MUTED }}>{p.photos?.length || 0} photo{(p.photos?.length || 0) > 1 ? 's' : ''} · {p.createdAt ? new Date(p.createdAt).toLocaleDateString('fr-CA') : '—'}</p>
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                      <button onClick={() => approvePhoto(p.uid)} disabled={!!photoWorking}
                        style={{ ...btn('rgba(74,222,128,0.12)', '#4ade80'), opacity: photoWorking ? 0.5 : 1 }}>
                        {isApproving ? '…' : '✓ Approuver'}
                      </button>
                      <button onClick={() => { if (window.confirm(`Rejeter et supprimer toutes les photos de ${p.name || p.uid} ?`)) rejectPhoto(p.uid); }} disabled={!!photoWorking}
                        style={{ ...btn('rgba(239,68,68,0.1)', '#f87171'), opacity: photoWorking ? 0.5 : 1 }}>
                        {isRejecting ? '…' : '✕ Rejeter'}
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8 }}>
                    {(p.photos || []).map((url, i) => (
                      <img key={i} src={url} alt="" onClick={() => window.open(url, '_blank')}
                        style={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover', borderRadius: 10, cursor: 'pointer', border: `1px solid ${BDR}`, transition: 'transform 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── ORDERS TAB ── */}
      {tab === 'orders' && (
        <div>
          {orders.length === 0 ? (
            <p style={{ color: MUTED }}>Aucun achat enregistré pour l'instant.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, border: `1px solid ${BDR}`, borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 100px 120px 110px', gap: 0, padding: '10px 16px', background: 'rgba(255,255,255,0.03)', borderBottom: `1px solid ${BDR}`, fontSize: '0.7rem', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <span>Produit</span><span>UID utilisateur</span><span>Montant</span><span>Devise</span><span>Date</span>
              </div>
              {orders.map((o, i) => (
                <div key={o.id} style={{ display: 'grid', gridTemplateColumns: '1fr 160px 100px 120px 110px', gap: 0, padding: '12px 16px', borderBottom: i < orders.length - 1 ? `1px solid ${BDR}` : 'none', alignItems: 'center' }}>
                  <div>
                    <p style={{ fontWeight: 700, fontSize: '0.88rem', color: WHITE, margin: 0 }}>{o.productName || o.product}</p>
                    {o.stripeInvoiceId && (
                      <p style={{ fontSize: '0.72rem', color: '#a78bfa', margin: '2px 0 0' }}>Facture Stripe ✓</p>
                    )}
                  </div>
                  <span style={{ fontSize: '0.72rem', color: MUTED, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.uid}</span>
                  <span style={{ fontWeight: 800, color: '#4ade80' }}>{((o.amount || 0) / 100).toFixed(2)} $</span>
                  <span style={{ fontSize: '0.78rem', color: MUTED, textTransform: 'uppercase' }}>{o.currency || 'cad'}</span>
                  <span style={{ fontSize: '0.76rem', color: MUTED }}>{fmt(o.at)}</span>
                </div>
              ))}
              <div style={{ padding: '12px 16px', background: 'rgba(167,139,250,0.06)', borderTop: `1px solid ${BDR}`, display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: '0.78rem', color: MUTED }}>Total ({orders.length} achat{orders.length > 1 ? 's' : ''}) :</span>
                <span style={{ fontWeight: 900, fontSize: '1.05rem', color: '#a78bfa' }}>{(stats.revenue / 100).toFixed(2)} $CAD</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── MODAL VIEW PROFILE ── */}
      {modal?.type === 'view' && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setModal(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#0d0d0d', border: `1px solid ${BDR}`, borderRadius: 20, padding: '28px', maxWidth: 480, width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', gap: 16, marginBottom: 20, alignItems: 'flex-start' }}>
              <img src={avatar(modal.user)} alt="" style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
              <div>
                <h2 style={{ margin: 0, fontWeight: 900, fontSize: '1.2rem' }}>{modal.user.name || '—'}</h2>
                <p style={{ margin: '4px 0 0', color: MUTED, fontSize: '0.82rem' }}>{modal.user.email || modal.user.uid}</p>
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  {isBoosted(modal.user) && <Badge label="⚡ Boosté" color="#000" bg={ACC} />}
                  {modal.user.banned    && <Badge label="Banni"    color="#fff" bg="#EF4444" />}
                </div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              {[['Âge', modal.user.age], ['Genre', modal.user.gender], ['Cherche', modal.user.lookingFor], ['Ville', modal.user.city], ['Inscrit', fmt(modal.user.createdAt)]].map(([k, v]) => (
                <div key={k} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '10px 14px' }}>
                  <div style={{ fontSize: '0.68rem', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{k}</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{v || '—'}</div>
                </div>
              ))}
            </div>
            {modal.user.bio && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: '0.68rem', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Bio</div>
                <p style={{ fontSize: '0.86rem', color: WHITE, lineHeight: 1.6, margin: 0 }}>{modal.user.bio}</p>
              </div>
            )}
            {modal.user.interests?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: '0.68rem', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Intérêts</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {modal.user.interests.map(t => (
                    <span key={t} style={{ padding: '4px 12px', borderRadius: 20, background: 'rgba(255,31,106,0.12)', border: '1px solid rgba(255,31,106,0.2)', fontSize: '0.78rem', color: PINK }}>{t}</span>
                  ))}
                </div>
              </div>
            )}
            {modal.user.photos?.length > 0 && (
              <div>
                <div style={{ fontSize: '0.68rem', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Photos ({modal.user.photos.length})</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                  {modal.user.photos.map((url, i) => (
                    <img key={i} src={url} alt="" style={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover', borderRadius: 10, cursor: 'pointer' }}
                      onClick={() => window.open(url, '_blank')} />
                  ))}
                </div>
              </div>
            )}
            <button onClick={() => setModal(null)} style={{ ...btn('rgba(255,255,255,0.08)', WHITE), width: '100%', marginTop: 20, padding: '11px' }}>Fermer</button>
          </div>
        </div>
      )}

      {/* ── MODAL BOOST ── */}
      {modal?.type === 'boost' && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setModal(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#0d0d0d', border: `1px solid ${BDR}`, borderRadius: 20, padding: '28px', maxWidth: 380, width: '100%' }}>
            <h3 style={{ margin: '0 0 6px', fontWeight: 900 }}>⚡ Booster {modal.user.name}</h3>
            <p style={{ color: MUTED, fontSize: '0.84rem', marginBottom: 20 }}>Le profil apparaîtra en tête des découvertes pendant la durée choisie.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {BOOST_OPTIONS.map(o => (
                <button key={o.hours} onClick={() => setBoostHours(o.hours)}
                  style={{ padding: '12px 16px', borderRadius: 12, border: `1.5px solid ${boostHours === o.hours ? ACC : BDR}`, background: boostHours === o.hours ? 'rgba(200,255,0,0.08)' : 'transparent', color: boostHours === o.hours ? ACC : MUTED, fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', transition: 'all 0.15s' }}>
                  {o.label}
                </button>
              ))}
            </div>
            {isBoosted(modal.user) && (
              <button onClick={() => { removeBoost(modal.user.id); setModal(null); }}
                style={{ ...btn('rgba(248,113,113,0.12)', '#f87171'), width: '100%', padding: '11px', marginBottom: 10 }}>
                Retirer le boost actuel
              </button>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setModal(null)} style={{ ...btn('rgba(255,255,255,0.07)', MUTED), flex: 1, padding: '11px' }}>Annuler</button>
              <button onClick={applyBoost} disabled={working} style={{ ...btn(ACC, '#000'), flex: 2, padding: '11px', opacity: working ? 0.6 : 1 }}>
                {working ? 'Application…' : '⚡ Appliquer le boost'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CONFIRM DELETE ── */}
      {confirmDel && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setConfirmDel(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#0d0d0d', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 20, padding: '28px', maxWidth: 360, width: '100%', textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🗑️</div>
            <h3 style={{ margin: '0 0 8px', fontWeight: 900 }}>Supprimer {confirmDel.name} ?</h3>
            <p style={{ color: MUTED, fontSize: '0.84rem', marginBottom: 24, lineHeight: 1.5 }}>
              Le profil Vybme <strong style={{ color: WHITE }}>et le compte Firebase</strong> seront supprimés définitivement. Cette action est irréversible.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setConfirmDel(null)} style={{ ...btn('rgba(255,255,255,0.07)', MUTED), flex: 1, padding: '11px' }}>Annuler</button>
              <button onClick={() => deleteUser(confirmDel)} style={{ ...btn('#EF4444', '#fff'), flex: 1, padding: '11px' }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
